import React, { useState, useEffect } from 'react';
import Table, { Column } from 'ui/Table';
import StatCard from 'ui/StatCard';
import Loading from 'ui/Loading';
import Button from 'ui/Button';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import StatsGrid from 'ui/StatsGrid';
import FilterSection from 'ui/FilterSection';
import TableContainer from 'ui/TableContainer';
import SearchInput from 'ui/SearchInput';
import { PlusIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import './style.scss';

interface PerformanceRecord {
    id: number;
    order_number: string;
    project_name: string;
    contract_amount: number;
    contract_date: string;
    completion_date?: string;
    location: string;
    client: string;
    project_type: string;
    status: 'completed' | 'in_progress' | 'planned';
    description?: string;
    construction_area?: number;
    duration_months?: number;
}

interface PerformanceForm {
    order_number: string;
    project_name: string;
    contract_amount: number;
    contract_date: string;
    completion_date: string;
    location: string;
    client: string;
    project_type: string;
    description: string;
    construction_area: number;
    duration_months: number;
}

const PerformanceManagementPage: React.FC = () => {
    const [records, setRecords] = useState<PerformanceRecord[]>([]);
    const [filteredRecords, setFilteredRecords] = useState<PerformanceRecord[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingRecord, setEditingRecord] = useState<PerformanceRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [performanceForm, setPerformanceForm] = useState<PerformanceForm>({
        order_number: '',
        project_name: '',
        contract_amount: 0,
        contract_date: '',
        completion_date: '',
        location: '',
        client: '',
        project_type: '건설사업',
        description: '',
        construction_area: 0,
        duration_months: 0
    });

    // Mock data based on the image
    useEffect(() => {
        const mockRecords: PerformanceRecord[] = [
            {
                id: 1,
                order_number: '1',
                project_name: '수익1구역 도시환경정비사업',
                contract_amount: 160962,
                contract_date: '2011-12-14',
                completion_date: '2014-11-20',
                location: '인천',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'completed',
                construction_area: 1005,
                duration_months: 36
            },
            {
                id: 2,
                order_number: '2',
                project_name: '동례 제2구역 주택재개발사업',
                contract_amount: 135109,
                contract_date: '2012-03-19',
                completion_date: '2018-12-26',
                location: '서울',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'completed',
                construction_area: 834,
                duration_months: 81
            },
            {
                id: 3,
                order_number: '3',
                project_name: '녹번제1구역재건축 주택재개발사업',
                contract_amount: 188359,
                contract_date: '2012-10-25',
                completion_date: '2018-12-06',
                location: '서울',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'completed',
                construction_area: 1240,
                duration_months: 74
            },
            {
                id: 4,
                order_number: '4',
                project_name: '동선제2구역 주택재개발사업',
                contract_amount: 51802,
                contract_date: '2012-11-30',
                location: '서울',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'in_progress',
                construction_area: 327,
                duration_months: 48
            },
            {
                id: 5,
                order_number: '5',
                project_name: '녹번제1구역1지구 주택재개발사업',
                contract_amount: 128603,
                contract_date: '2014-07-31',
                completion_date: '2018-08-31',
                location: '서울',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'completed',
                construction_area: 899,
                duration_months: 49
            },
            {
                id: 6,
                order_number: '6',
                project_name: '고양연담지구 주택재개발사업',
                contract_amount: 135710,
                contract_date: '2014-12-29',
                completion_date: '2018-10-15',
                location: '경기',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'completed',
                construction_area: 1005,
                duration_months: 46
            },
            {
                id: 7,
                order_number: '7',
                project_name: '부개3구역 주택재개발사업',
                contract_amount: 63432,
                contract_date: '2015-02-12',
                completion_date: '2021-07-29',
                location: '인천',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'completed',
                construction_area: 532,
                duration_months: 77
            },
            {
                id: 8,
                order_number: '8',
                project_name: '용답제4구역 주택재건축사업',
                contract_amount: 54360,
                contract_date: '2015-04-08',
                completion_date: '2021-11-03',
                location: '서울',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'completed',
                construction_area: 352,
                duration_months: 79
            },
            {
                id: 9,
                order_number: '9',
                project_name: '면목1주택재개발정비사업',
                contract_amount: 73098,
                contract_date: '2015-05-08',
                completion_date: '2019-11-04',
                location: '서울',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'completed',
                construction_area: 497,
                duration_months: 54
            },
            {
                id: 10,
                order_number: '10',
                project_name: '신수1구역 주택재건축사업',
                contract_amount: 167122,
                contract_date: '2015-09-15',
                completion_date: '2019-08-14',
                location: '서울',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'completed',
                construction_area: 1015,
                duration_months: 47
            },
            {
                id: 11,
                order_number: '11',
                project_name: '창동남지구 주택재개발사업',
                contract_amount: 183985,
                contract_date: '2015-10-22',
                location: '인천',
                client: '소방/통신',
                project_type: '건설사업',
                status: 'in_progress',
                construction_area: 1216,
                duration_months: 60
            }
        ];
        setRecords(mockRecords);
        setFilteredRecords(mockRecords);
        setLoading(false);
    }, []);

    // 검색 및 필터링
    useEffect(() => {
        let filtered = records;

        if (searchTerm) {
            filtered = filtered.filter(record => 
                record.project_name.includes(searchTerm) || 
                record.client.includes(searchTerm) ||
                record.location.includes(searchTerm) ||
                record.order_number.includes(searchTerm)
            );
        }

        if (selectedType !== 'all') {
            filtered = filtered.filter(record => record.project_type === selectedType);
        }

        if (selectedStatus !== 'all') {
            filtered = filtered.filter(record => record.status === selectedStatus);
        }

        setFilteredRecords(filtered);
    }, [records, searchTerm, selectedType, selectedStatus]);

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed': return '완료';
            case 'in_progress': return '진행중';
            case 'planned': return '계획중';
            default: return status;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return '#28a745';
            case 'in_progress': return '#ffc107';
            case 'planned': return '#17a2b8';
            default: return '#6c757d';
        }
    };

    const columns: Column<PerformanceRecord>[] = [
        {
            key: 'order_number',
            header: '번호',
            width: '80px',
            align: 'center',
            sortable: true
        },
        {
            key: 'project_name',
            header: '용역명',
            sortable: true
        },
        {
            key: 'contract_amount',
            header: '계약금액(천원)',
            width: '120px',
            align: 'right',
            sortable: true,
            render: (value) => value?.toLocaleString() || '-'
        },
        {
            key: 'contract_date',
            header: '계약일자',
            width: '110px',
            align: 'center',
            sortable: true
        },
        {
            key: 'completion_date',
            header: '준공일자',
            width: '110px',
            align: 'center',
            render: (value) => value || '-'
        },
        {
            key: 'location',
            header: '지역',
            width: '80px',
            align: 'center'
        },
        {
            key: 'client',
            header: '용역구분',
            width: '100px',
            align: 'center'
        },
        {
            key: 'status',
            header: '상태',
            width: '80px',
            align: 'center',
            render: (value) => (
                <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: getStatusColor(value) + '20',
                    color: getStatusColor(value)
                }}>
                    {getStatusLabel(value)}
                </span>
            )
        }
    ];

    const calculateStatus = (contractDate: string, completionDate?: string) => {
        if (completionDate) return 'completed';
        
        const contract = new Date(contractDate);
        const now = new Date();
        const daysDiff = (now.getTime() - contract.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysDiff > 0) return 'in_progress';
        return 'planned';
    };

    const handleCreate = () => {
        setEditingRecord(null);
        setPerformanceForm({
            order_number: '',
            project_name: '',
            contract_amount: 0,
            contract_date: '',
            completion_date: '',
            location: '',
            client: '',
            project_type: '건설사업',
            description: '',
            construction_area: 0,
            duration_months: 0
        });
        setShowCreateModal(true);
    };

    const handleEdit = (record: PerformanceRecord) => {
        setEditingRecord(record);
        setPerformanceForm({
            order_number: record.order_number,
            project_name: record.project_name,
            contract_amount: record.contract_amount,
            contract_date: record.contract_date,
            completion_date: record.completion_date || '',
            location: record.location,
            client: record.client,
            project_type: record.project_type,
            description: record.description || '',
            construction_area: record.construction_area || 0,
            duration_months: record.duration_months || 0
        });
        setShowCreateModal(true);
    };

    const handleSave = () => {
        const newStatus = calculateStatus(performanceForm.contract_date, performanceForm.completion_date);
        
        if (editingRecord) {
            // 수정
            const updatedRecords = records.map(record => 
                record.id === editingRecord.id 
                    ? { 
                        ...record, 
                        ...performanceForm,
                        status: newStatus as PerformanceRecord['status']
                    }
                    : record
            );
            setRecords(updatedRecords);
        } else {
            // 새로 생성
            const newRecord: PerformanceRecord = {
                id: Math.max(...records.map(r => r.id)) + 1,
                ...performanceForm,
                status: newStatus as PerformanceRecord['status']
            };
            setRecords([...records, newRecord]);
        }
        
        setShowCreateModal(false);
        alert('실적 정보가 저장되었습니다.');
    };

    const handleDelete = (recordId: number) => {
        if (window.confirm('이 실적 정보를 삭제하시겠습니까?')) {
            setRecords(records.filter(record => record.id !== recordId));
        }
    };

    const getStatistics = () => {
        const total = records.length;
        const totalAmount = records.reduce((sum, record) => sum + record.contract_amount, 0);
        const completed = records.filter(r => r.status === 'completed').length;
        const inProgress = records.filter(r => r.status === 'in_progress').length;
        const totalArea = records.reduce((sum, record) => sum + (record.construction_area || 0), 0);
        
        return { total, totalAmount, completed, inProgress, totalArea };
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ko-KR').format(amount);
    };

    const stats = getStatistics();

    if (loading) {
        return (
            <PageContainer>
                <Loading text="실적 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer className="performance-page">
            <PageHeader
                title="실적 관리"
                subtitle="프로젝트 실적 현황 및 관리"
                actions={
                    <>
                        <Button variant="secondary" icon={<ArrowDownTrayIcon />}>
                            실적표 다운로드
                        </Button>
                        <Button variant="primary" icon={<PlusIcon />} onClick={handleCreate}>
                            실적 등록
                        </Button>
                    </>
                }
            />

            <StatsGrid>
                <StatCard
                    title="총 프로젝트"
                    value={stats.total}
                    suffix="건"
                    iconColor="total"
                    icon={<svg fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z"/>
                        <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd"/>
                    </svg>}
                />

                <StatCard
                    title="총 계약금액"
                    value={formatCurrency(stats.totalAmount)}
                    suffix="만원"
                    iconColor="success"
                    icon={<svg fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                    </svg>}
                />

                <StatCard
                    title="완료 프로젝트"
                    value={stats.completed}
                    suffix="건"
                    iconColor="primary"
                    valueColor="primary"
                    icon={<svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>}
                />

                <StatCard
                    title="진행중 프로젝트"
                    value={stats.inProgress}
                    suffix="건"
                    iconColor="warning"
                    valueColor="warning"
                    icon={<svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/>
                    </svg>}
                />
            </StatsGrid>

            <FilterSection>
                <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="프로젝트명, 발주처, 지역, 번호로 검색..."
                />
                <select 
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="form-select"
                >
                    <option value="all">전체 유형</option>
                    <option value="건설사업">건설사업</option>
                    <option value="전기공사">전기공사</option>
                    <option value="소방공사">소방공사</option>
                    <option value="정보통신공사">정보통신공사</option>
                </select>
                <select 
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="form-select"
                >
                    <option value="all">전체 상태</option>
                    <option value="completed">완료</option>
                    <option value="in_progress">진행중</option>
                    <option value="planned">계획중</option>
                </select>
            </FilterSection>

            <TableContainer
                title="실적 목록"
                resultCount={filteredRecords.length}
            >
                <Table
                    columns={columns}
                    data={filteredRecords}
                    pagination
                    pageSize={15}
                    defaultSortKey="order_number"
                    defaultSortOrder="asc"
                    stickyHeader
                    maxHeight="600px"
                    actions={(record) => (
                        <div className="action-buttons">
                            <button 
                                className="btn btn-sm btn-edit"
                                onClick={() => handleEdit(record)}
                            >
                                수정
                            </button>
                            <button 
                                className="btn btn-sm btn-delete"
                                onClick={() => handleDelete(record.id)}
                            >
                                삭제
                            </button>
                        </div>
                    )}
                />
            </TableContainer>

            {/* 실적 등록/수정 모달 */}
            {showCreateModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>{editingRecord ? '실적 수정' : '실적 등록'}</h3>
                            <button 
                                className="modal-close"
                                onClick={() => setShowCreateModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>번호</label>
                                    <input
                                        type="text"
                                        value={performanceForm.order_number}
                                        onChange={(e) => setPerformanceForm({...performanceForm, order_number: e.target.value})}
                                        placeholder="프로젝트 번호"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>공종</label>
                                    <select
                                        value={performanceForm.project_type}
                                        onChange={(e) => setPerformanceForm({...performanceForm, project_type: e.target.value})}
                                    >
                                        <option value="건설사업">건설사업</option>
                                        <option value="전기공사">전기공사</option>
                                        <option value="소방공사">소방공사</option>
                                        <option value="정보통신공사">정보통신공사</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>사업명</label>
                                <input
                                    type="text"
                                    value={performanceForm.project_name}
                                    onChange={(e) => setPerformanceForm({...performanceForm, project_name: e.target.value})}
                                    placeholder="프로젝트명을 입력하세요"
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>발주처</label>
                                    <input
                                        type="text"
                                        value={performanceForm.client}
                                        onChange={(e) => setPerformanceForm({...performanceForm, client: e.target.value})}
                                        placeholder="발주처명을 입력하세요"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>지역</label>
                                    <input
                                        type="text"
                                        value={performanceForm.location}
                                        onChange={(e) => setPerformanceForm({...performanceForm, location: e.target.value})}
                                        placeholder="지역을 입력하세요"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>계약금액 (만원)</label>
                                    <input
                                        type="number"
                                        value={performanceForm.contract_amount}
                                        onChange={(e) => setPerformanceForm({...performanceForm, contract_amount: Number(e.target.value)})}
                                        placeholder="계약금액을 입력하세요"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>연면적 (㎡)</label>
                                    <input
                                        type="number"
                                        value={performanceForm.construction_area}
                                        onChange={(e) => setPerformanceForm({...performanceForm, construction_area: Number(e.target.value)})}
                                        placeholder="연면적을 입력하세요"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>계약일</label>
                                    <input
                                        type="date"
                                        value={performanceForm.contract_date}
                                        onChange={(e) => setPerformanceForm({...performanceForm, contract_date: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>준공일 (선택)</label>
                                    <input
                                        type="date"
                                        value={performanceForm.completion_date}
                                        onChange={(e) => setPerformanceForm({...performanceForm, completion_date: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>공사기간 (개월)</label>
                                <input
                                    type="number"
                                    value={performanceForm.duration_months}
                                    onChange={(e) => setPerformanceForm({...performanceForm, duration_months: Number(e.target.value)})}
                                    placeholder="예상 공사기간을 입력하세요"
                                />
                            </div>
                            <div className="form-group">
                                <label>설명</label>
                                <textarea
                                    value={performanceForm.description}
                                    onChange={(e) => setPerformanceForm({...performanceForm, description: e.target.value})}
                                    placeholder="프로젝트에 대한 설명을 입력하세요"
                                    rows={4}
                                />
                            </div>
                            {performanceForm.contract_date && (
                                <div className="status-preview">
                                    <h4>예상 상태</h4>
                                    <span className={`status-badge ${getStatusColor(calculateStatus(performanceForm.contract_date, performanceForm.completion_date))}`}>
                                        {getStatusLabel(calculateStatus(performanceForm.contract_date, performanceForm.completion_date))}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary"
                                onClick={() => setShowCreateModal(false)}
                            >
                                취소
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={!performanceForm.project_name || !performanceForm.client}
                            >
                                {editingRecord ? '수정' : '등록'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageContainer>
    );
};

export default PerformanceManagementPage;
